import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpResponse, HttpErrorResponse, HttpContextToken } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { TokenRefreshService } from '../_services/token-refresh/token-refresh.service';

// Set on the retry after a renewal, so a second 401 goes to /login instead of renewing again.
const RETRIED_AFTER_RENEWAL = new HttpContextToken<boolean>(() => false);

@Injectable({
    providedIn: 'root'
})
export class UnauthorisedUserLogoutInterceptor implements HttpInterceptor {

    constructor(private router: Router, private tokenRefresh: TokenRefreshService) { }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

        if (!request.headers.has('Content-Type')) {
            request = request.clone({ headers: request.headers.set('Content-Type', 'application/json') });
        }

        request = request.clone({ headers: request.headers.set('Accept', 'application/json') });

        // A token already past its expiry is renewed before the request goes, rather than sent to be refused
        // with a 401 first (bug 40427). The request then counts as the retry, so a refusal signs the user out.
        const token = this.renewableToken(request);
        if (token && TokenRefreshService.tokenExpired(token)) {
            return this.tokenRefresh.renew(token).pipe(switchMap(renewed => this.send(request.clone({
                headers: renewed ? request.headers.set('Authorization', 'Bearer ' + renewed) : request.headers,
                context: request.context.set(RETRIED_AFTER_RENEWAL, true)
            }), next)));
        }

        return this.send(request, next);
    }

    private send(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(request).pipe(tap((event: HttpEvent<any>) => {
            if (event instanceof HttpResponse) {
                if (event.status == 401) {
                    this.router.navigate(['/login']);
                }
            }
        }),
            catchError((err: HttpErrorResponse) => {
                if (err && err.status == 401) {
                    const expiredToken = this.renewableToken(request);
                    if (expiredToken) {
                        // The token has expired: renew it once and send the same request again.
                        return this.tokenRefresh.renew(expiredToken).pipe(switchMap(token => {
                            if (!token) {
                                this.router.navigate(['/login']);
                                return throwError(() => err);
                            }
                            const retry = request.clone({
                                headers: request.headers.set('Authorization', 'Bearer ' + token),
                                context: request.context.set(RETRIED_AFTER_RENEWAL, true)
                            });
                            return this.send(retry, next);
                        }));
                    }
                    this.router.navigate(['/login']);
                }
                return throwError(() => err);
            })
        );
    }

    // The bearer token of a first attempt at our own API; null for anything that should not renew.
    private renewableToken(request: HttpRequest<any>): string | null {
        const authorization = request.headers.get('Authorization');
        if (request.context.get(RETRIED_AFTER_RENEWAL) || !request.url.startsWith(environment.apiUrl)
            || !authorization?.startsWith('Bearer ')) {
            return null;
        }
        return authorization.substring('Bearer '.length);
    }

}
